import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from "@mui/material";

import { changeSubscriptionPlan } from "../../../api";
import { logError } from "../../../logging/loggingService";
import { formatPlanPrice } from "./formatters";

export default function PlanChangeDialog({
    open,
    onClose,
    planOptions,
    planOptionsLoading,
    planOptionsError,
    onRefreshUser,
    onNotify,
}) {
    const [planChangeSubmitting, setPlanChangeSubmitting] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState("");

    const availableTargetPlans = useMemo(() => planOptions.filter((plan) => plan?.value), [planOptions]);

    useEffect(() => {
        if (!open) {
            return;
        }
        if (!availableTargetPlans.length) {
            setSelectedPlan("");
            return;
        }
        setSelectedPlan((prev) => {
            if (prev && availableTargetPlans.some((plan) => plan.value === prev)) {
                return prev;
            }
            return availableTargetPlans[0].value;
        });
    }, [open, availableTargetPlans]);

    const handleClose = () => {
        if (planChangeSubmitting) return;
        onClose();
    };

    const handleSubmitPlanChange = async () => {
        if (!selectedPlan) {
            onNotify("Bitte wähle einen Tarif aus.", "warning");
            return;
        }
        setPlanChangeSubmitting(true);
        try {
            const res = await changeSubscriptionPlan({ targetPlan: selectedPlan });
            if (res.status >= 200 && res.status < 300) {
                const checkoutUrl = res.data?.checkoutUrl;
                onNotify("Tarifwechsel gestartet. Bitte schließe die Zahlung im neuen Fenster ab.", "success");
                onClose();
                await onRefreshUser();
                if (checkoutUrl && typeof window !== "undefined") {
                    window.open(checkoutUrl, "_blank", "noopener,noreferrer");
                }
            }
            const message =
                typeof res.data === "string" && res.data.trim().length > 0
                    ? res.data
                    : "Tarifwechsel konnte nicht gestartet werden.";
            onNotify(message, "error");
        } catch (err) {
            logError("Fehler beim Tarifwechsel", err, { component: "Account" });
            const backendMessage = err?.response?.data;
            const message =
                typeof backendMessage === "string" && backendMessage.trim().length > 0
                    ? backendMessage
                    : "Tarifwechsel konnte nicht gestartet werden.";
            onNotify(message, "error");
        } finally {
            setPlanChangeSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Tarif wechseln</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    Wähle den gewünschten Tarif. Wir leiten dich anschließend zur Zahlungsbestätigung weiter.
                </DialogContentText>
                {planOptionsLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : planOptionsError ? (
                    <Alert severity="error">{planOptionsError}</Alert>
                ) : availableTargetPlans.length === 0 ? (
                    <Alert severity="info">Es sind aktuell keine alternativen Tarife verfügbar.</Alert>
                ) : (
                    <FormControl component="fieldset" sx={{ width: "100%" }}>
                        <RadioGroup value={selectedPlan} onChange={(event) => setSelectedPlan(event.target.value)}>
                            {availableTargetPlans.map((plan) => {
                                const selected = selectedPlan === plan.value;
                                return (
                                    <FormControlLabel
                                        key={plan.value}
                                        value={plan.value}
                                        control={<Radio />}
                                        sx={{
                                            m: 0,
                                            width: "100%",
                                            alignItems: "flex-start",
                                            border: "1px solid",
                                            borderColor: selected ? "primary.main" : "divider",
                                            borderRadius: 2,
                                            px: 2,
                                            py: 1.5,
                                            mb: 1.5,
                                            backgroundColor: selected ? "action.hover" : "background.paper",
                                            boxShadow: selected ? "0 6px 20px rgba(25,118,210,0.12)" : "none",
                                        }}
                                        label={
                                            <Stack spacing={0.5}>
                                                <Typography variant="subtitle1" fontWeight={600}>
                                                    {plan.description}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {plan.tierLabel} – {plan.cycleLabel}
                                                </Typography>
                                                <Typography variant="body2">
                                                    Preis: {formatPlanPrice(plan.amount, plan.yearly)}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color={plan.accountingAccess ? "success.main" : "text.secondary"}
                                                >
                                                    {plan.accountingAccess
                                                        ? "Enthält Buchhaltungsfunktionen"
                                                        : "Enthält Rechnungsmodul"}
                                                </Typography>
                                            </Stack>
                                        }
                                    />
                                );
                            })}
                        </RadioGroup>
                    </FormControl>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={planChangeSubmitting}>
                    Abbrechen
                </Button>
                <Button onClick={handleSubmitPlanChange} variant="contained" disabled={planChangeSubmitting || !selectedPlan}>
                    {planChangeSubmitting ? <CircularProgress size={22} color="inherit" /> : "Weiter zur Zahlung"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
